import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api } from '../config';
import { useUser } from '../contexts/UserContext';
import { getDivisions } from '../utils/departments';
import './DelegateManagement.css';
import './ReviewerManagement.css';
import Spinner from './Spinner';
import '../styles/ResizableTable.css';
import { initResizableTables } from '../utils/resizableTables';
import { useTableSort } from '../utils/useTableSort';

interface DQReviewer {
  id: number;
  email: string;
  display_name: string | null;
  division: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const DQReviewerManagement: React.FC = () => {
  const { user } = useUser();
  const divisions = getDivisions();

  const [reviewers, setReviewers] = useState<DQReviewer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newDivision, setNewDivision] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [updatingDivisionId, setUpdatingDivisionId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState('All');
  const itemsPerPage = 10;
  const [pageByCat, setPageByCat] = useState<Record<string, number>>({});

  useEffect(() => { loadReviewers(); }, []);

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => initResizableTables(), 50);
    }
  }, [isLoading]);

  const loadReviewers = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(api('/api/dq-reviewers/list'));
      setReviewers(response.data);
      setMessage(null);
    } catch {
      setMessage({ type: 'error', text: 'Failed to load DQ reviewers' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) { setMessage({ type: 'error', text: 'Please enter an email address' }); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) { setMessage({ type: 'error', text: 'Please enter a valid email address' }); return; }
    if (!newDivision) { setMessage({ type: 'error', text: 'Please select a division' }); return; }

    try {
      setIsAdding(true);
      await axios.post(api('/api/dq-reviewers/add'), {}, {
        params: { email: newEmail.trim().toLowerCase(), division: newDivision }
      });
      setNewEmail('');
      if (newDivision) setActiveTab(newDivision);
      setNewDivision('');
      setMessage({ type: 'success', text: 'DQ Reviewer added successfully' });
      setTimeout(() => setMessage(null), 3000);
      loadReviewers();
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message || 'Failed to add DQ reviewer';
      setMessage({ type: 'error', text: detail });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (id: number, email: string) => {
    if (!window.confirm(`Remove ${email} from DQ reviewers?`)) return;
    try {
      await axios.delete(api(`/api/dq-reviewers/remove/${id}`));
      setMessage({ type: 'success', text: 'DQ Reviewer removed successfully' });
      setTimeout(() => setMessage(null), 3000);
      loadReviewers();
    } catch {
      setMessage({ type: 'error', text: 'Failed to remove DQ reviewer' });
    }
  };

  const handleUpdateDivision = async (reviewer: DQReviewer, division: string) => {
    setUpdatingDivisionId(reviewer.id);
    try {
      await axios.put(api(`/api/dq-reviewers/update/${reviewer.id}`), {}, { params: { division } });
      setReviewers(prev => prev.map(r => r.id === reviewer.id ? { ...r, division } : r));
    } catch {
      setMessage({ type: 'error', text: 'Failed to update division' });
    } finally {
      setUpdatingDivisionId(null);
    }
  };

  const assignedDivisions = Array.from(
    new Set(reviewers.map(r => r.division).filter(Boolean) as string[])
  ).sort();
  const hasUnassigned = reviewers.some(r => !r.division);
  const tabs = ['All', ...assignedDivisions, ...(hasUnassigned ? ['Unassigned'] : [])];
  const effectiveTab = tabs.includes(activeTab) ? activeTab : 'All';

  const tabReviewers = reviewers.filter(r => {
    if (effectiveTab === 'All') return true;
    if (effectiveTab === 'Unassigned') return !r.division;
    return r.division === effectiveTab;
  });

  const currentPage = pageByCat[effectiveTab] || 1;
  const { sortedData: sortedReviewers, handleSort, sortIcon } = useTableSort<DQReviewer>(tabReviewers, 'email', 'asc', {
    created_at: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  });
  const totalPages = Math.ceil(sortedReviewers.length / itemsPerPage);
  const paginated = sortedReviewers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const setPage = (p: number) => setPageByCat(prev => ({ ...prev, [effectiveTab]: p }));

  if (isLoading) {
    return (
      <div className="delegate-management">
        <Spinner message="Loading DQ reviewers..." />
      </div>
    );
  }

  return (
    <div className="delegate-management">
      {message && <div className={`message-alert ${message.type}`}>{message.text}</div>}

      <div className="add-delegate-section">
        <h3>Add New DQ Reviewer</h3>
        <form onSubmit={handleAdd} className="add-delegate-form" style={{ flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ minWidth: '220px' }}>
            <label htmlFor="dq-reviewer-email">Email Address:</label>
            <input
              id="dq-reviewer-email"
              type="email"
              placeholder="reviewer@example.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              disabled={isAdding}
              className="email-input"
            />
          </div>
          <div className="form-group" style={{ minWidth: '150px' }}>
            <label htmlFor="dq-reviewer-division">Division:</label>
            <select
              id="dq-reviewer-division"
              value={newDivision}
              onChange={e => setNewDivision(e.target.value)}
              disabled={isAdding}
              className="email-input"
            >
              <option value="">--- Select ---</option>
              {divisions.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ minWidth: '120px', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={isAdding} className="btn-add-delegate" style={{ marginTop: '22px' }}>
              {isAdding ? 'Adding...' : 'Add DQ Reviewer'}
            </button>
          </div>
        </form>
      </div>

      <div className="reviewer-division-tabs">
        {tabs.map(tab => (
          <button
            key={tab}
            className={`reviewer-div-tab${effectiveTab === tab ? ' reviewer-div-tab--active' : ''}`}
            onClick={() => { setActiveTab(tab); setPage(1); }}
          >
            {tab}
            <span className="reviewer-div-tab-count">
              {tab === 'All' ? reviewers.length
                : tab === 'Unassigned' ? reviewers.filter(r => !r.division).length
                : reviewers.filter(r => r.division === tab).length}
            </span>
          </button>
        ))}
      </div>

      <div className="delegates-list-section" style={{ marginTop: 0 }}>
        {tabReviewers.length === 0 ? (
          <div className="empty-state"><p>No DQ reviewers in this division.</p></div>
        ) : (
          <>
            <div className="delegates-table-container">
              <table id="manage-dq-reviewers-table" className="delegates-table">
                <thead>
                  <tr>
                    <th className="sortable-th" onClick={() => handleSort('email')}>Email {sortIcon('email')}</th>
                    <th className="sortable-th" onClick={() => handleSort('display_name')}>Display Name {sortIcon('display_name')}</th>
                    <th className="sortable-th" onClick={() => handleSort('division')}>Division {sortIcon('division')}</th>
                    <th className="sortable-th" onClick={() => handleSort('created_at')}>Added Date {sortIcon('created_at')}</th>
                    <th className="sortable-th" onClick={() => handleSort('is_active')}>Status {sortIcon('is_active')}</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(reviewer => (
                    <tr key={reviewer.id} className="delegate-row">
                      <td className="email-cell">{reviewer.email}</td>
                      <td className="name-cell">
                        {reviewer.display_name || <span style={{ color: '#999' }}>Not logged in yet</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <select
                            value={reviewer.division || ''}
                            onChange={e => handleUpdateDivision(reviewer, e.target.value)}
                            disabled={updatingDivisionId === reviewer.id}
                            className="email-input"
                            style={{ padding: '4px 8px', height: '32px', fontSize: '13px' }}
                          >
                            <option value="">--- None ---</option>
                            {divisions.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                          {updatingDivisionId === reviewer.id && (
                            <span style={{
                              display: 'inline-block',
                              width: '16px', height: '16px',
                              border: '2px solid #ddd',
                              borderTopColor: 'var(--abb-red)',
                              borderRadius: '50%',
                              animation: 'spin 0.7s linear infinite',
                              flexShrink: 0
                            }} />
                          )}
                        </div>
                      </td>
                      <td className="date-cell">
                        {new Date(reviewer.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge ${reviewer.is_active ? 'active' : 'inactive'}`}>
                          {reviewer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="action-cell">
                        <button className="btn-remove" onClick={() => handleRemove(reviewer.id, reviewer.email)} title="Remove DQ reviewer">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button className="pagination-btn" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>← Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    className={`pagination-btn${currentPage === p ? ' active' : ''}`}
                    onClick={() => setPage(p)}
                  >{p}</button>
                ))}
                <button className="pagination-btn" onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>Next →</button>
                <span className="pagination-info">{tabReviewers.length} DQ reviewer{tabReviewers.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DQReviewerManagement;
